import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ROUTES } from '../constants';
import { Step } from 'react-joyride';
import { useLanguage } from './LanguageContext';
import { getDashboardForRole } from '../data/onboardingSteps';

interface OnboardingContextType {
  run: boolean;
  stepIndex: number;
  hasCompleted: boolean;
  startTour: () => void;
  setStepIndex: (index: number) => void;
  setRun: (run: boolean) => void;
  dismiss: () => void;
  finish: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// Roles that should NOT get onboarding
const EXCLUDED_ROLES = ['waiter', 'admin'];

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(true);

  useEffect(() => {
    if (user) {
      const storageKey = `onboarding_completed_${user.id}`;
      const completed = localStorage.getItem(storageKey);
      
      if (!completed) {
        setHasCompleted(false);
        // Auto-start tour for all roles except excluded ones
        if (!EXCLUDED_ROLES.includes(user.role)) {
          const timer = setTimeout(() => {
            setRun(true);
          }, 1000);
          return () => clearTimeout(timer);
        }
      } else {
        setHasCompleted(true);
      }
    } else {
      setRun(false);
      setHasCompleted(true);
    }
  }, [user]);

  const startTour = () => {
    setStepIndex(0);
    setRun(true);
  };

  const dismiss = () => {
    if (user) {
      const storageKey = `onboarding_completed_${user.id}`;
      localStorage.setItem(storageKey, 'true');
    }
    setRun(false);
    setHasCompleted(true);
  };

  const finish = () => {
    if (user) {
      const storageKey = `onboarding_completed_${user.id}`;
      localStorage.setItem(storageKey, 'true');
    }
    setRun(false);
    setHasCompleted(true);
    
    // Navigate to the role's dashboard (not always the owner dashboard)
    if (user && location.pathname !== ROUTES.RESTAURANT_LEARNING_CENTER) {
      const dashboard = getDashboardForRole(user.role);
      navigate(dashboard);
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        run,
        stepIndex,
        hasCompleted,
        startTour,
        setStepIndex,
        setRun,
        dismiss,
        finish,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
